import { gql } from '@apollo/client'
import { navigate } from '@reach/router'
import * as Sentry from '@sentry/browser'
import * as React from 'react'
import styled, { css } from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { Link } from '../../../../common/src/components/Link'
import { ColoredSpan, Spacer, BodyTextSpan, BodyText } from '../../../../common/src/components/core'
import { ColorName, TZ_PACIFIC, Colors } from '../../../../common/src/constants'
import { TestIds } from '../../../../common/src/data/testids'
import { diffFmtSecs, timeHourFmt } from '../../../../common/src/date'
import memoize from '../../../../common/src/memoize'
import {
  FetchAllUsersQueryQuery,
  ClockOutUserMutationVariables,
  SetAutoStationEnabledForUserMutationVariables,
  UserType,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { lookAtUser } from '../../graphql/mutations'
import { dash, UserStatus } from './UserStatus'

const clockOutUserMutation = gql`
  mutation ClockOutUser($userId: Int!) {
    setClockedIn(userId: $userId, isClockedIn: false) {
      id
      user {
        id
      }
    }
  }
`

const setAutoStationEnabledForUserMutation = gql`
  mutation SetAutoStationEnabledForUser($userId: Int!, $enabled: Boolean!) {
    setAutoStationEnabledForUser(userId: $userId, enabled: $enabled) {
      id
      name
    }
  }
`

export interface OperatorDashboardRowProps {
  loggedInUser: FetchUserContextQuery['self']
  user: FetchAllUsersQueryQuery['allUsers'][0]
  columns: ColumnInfo[]
  timeNow: string
}

export interface OperatorDashboardRowState {
  forceDisableClockOutButton: boolean
  forceDisableAutoStationAssignmentToggle: boolean
  user: FetchAllUsersQueryQuery['allUsers'][0]
}

interface OperatorDashboardTableProps {
  users: FetchAllUsersQueryQuery['allUsers']
  timeNow: string
  loggedInUser: FetchUserContextQuery['self']
  showHeader: boolean
}

export default function OperatorDashboardTable(props: OperatorDashboardTableProps) {
  const { users, timeNow, loggedInUser, showHeader } = props
  const cols = operatorDashCols
  return (
    <>
      {showHeader ? (
        <HeaderRow>
          {cols.map((header, i) => (
            <Cell key={i} width={header.width} align={header.align}>
              {header.text}
            </Cell>
          ))}
        </HeaderRow>
      ) : null}

      {users.map(user => (
        <OperatorDashRow loggedInUser={loggedInUser} timeNow={timeNow} columns={cols} user={user} key={user.id} />
      ))}
    </>
  )
}

const operatorDashCols = [
  { text: '', width: 104, align: 'center' }, // which shift they're on
  { text: '', width: 220, align: 'left' },
  { text: 'unreads', width: 104, align: 'center' },
  { text: 'oldest', width: 104, align: 'center' },
  { text: 'status', width: 104, align: 'center' },
  { text: '', width: 104, align: 'center' },
  { text: '', width: 104, align: 'center' },
]

class OperatorDashRow extends React.Component<OperatorDashboardRowProps, OperatorDashboardRowState> {
  constructor(props: OperatorDashboardRowProps) {
    super(props)

    this.state = {
      forceDisableClockOutButton: false,
      forceDisableAutoStationAssignmentToggle: false,
      user: props.user,
    }
  }

  public static getDerivedStateFromProps(nextProps: OperatorDashboardRowProps, prevState: OperatorDashboardRowState) {
    const result = {
      forceDisableAutoStationAssignmentToggle: prevState.forceDisableAutoStationAssignmentToggle,
      forceDisableClockOutButton: prevState.forceDisableClockOutButton,
      user: nextProps.user,
    }

    if (nextProps.user.autoStationAssignmentEnabled !== prevState.user.autoStationAssignmentEnabled) {
      result.forceDisableAutoStationAssignmentToggle = false
    }

    if (nextProps.user.isClockedIn !== prevState.user.isClockedIn) {
      result.forceDisableClockOutButton = false
    }

    return result
  }

  private runMutationForThisUser(
    mutation: any,
    variables?: Omit<ClockOutUserMutationVariables | SetAutoStationEnabledForUserMutationVariables, 'userId'>
  ) {
    return apolloWebClient.mutate({
      mutation,
      variables: {
        userId: this.props.user.id,
        ...variables,
      },
    })
  }

  private renderOldestMessage = () => {
    const user = this.props.user
    const st = user.autoStationAssignmentEnabled
    const oldestMessageAgeMs =
      st &&
      user.oldestUnarchivedMessageTimeCreated &&
      Date.parse(this.props.timeNow) - Date.parse(user.oldestUnarchivedMessageTimeCreated)
    return oldestMessageAgeMs
      ? oldestMessageAgeMs < 1000 * 60 * 5
        ? '<5m'
        : diffFmtSecs(oldestMessageAgeMs / 1000)
      : dash()
  }

  private toggleAutoStation = () => {
    const { autoStationAssignmentEnabled, name } = this.props.user
    const nextEnabled = !autoStationAssignmentEnabled
    const question = `Are you sure you want to ${nextEnabled ? 'enable' : 'disable'} auto station for ${name}?`
    if (!confirm(question)) {
      return
    }

    const variables = {
      enabled: nextEnabled,
    }

    this.setState(
      {
        forceDisableAutoStationAssignmentToggle: true,
      },
      () => {
        this.runMutationForThisUser(setAutoStationEnabledForUserMutation, variables).catch(e => {
          Sentry.captureException(e)
        })
      }
    )
  }

  private handleClockOutClick = () => {
    if (confirm(`Are you sure you want to clock out ${this.props.user.name}?`)) {
      this.setState({ forceDisableClockOutButton: true }, () => {
        this.runMutationForThisUser(clockOutUserMutation).catch(Sentry.captureException)
      })
    }
  }

  /**
   * some users are able to modify the state of other users. we only show this
   * editing UI to those users who have permission to change that state.
   */
  private showSuperUI() {
    return this.props.loggedInUser.permissions.canViewSuperDashboard
  }

  private renderClockOutButton() {
    if (!this.showSuperUI()) {
      return dash()
    }

    return (
      <OverrideButton
        color={'midway'}
        disabled={this.state.forceDisableClockOutButton}
        onClick={this.handleClockOutClick}
        size="small"
      >
        {'CLOCK OUT'}
      </OverrideButton>
    )
  }

  private renderName = () => (
    <ColoredSpan color="midway">
      <Link onClick={this.onClickName(this.props.user.id)} to={'/leasing/inbox'} color="silver" opensNewTab={false}>
        {this.props.user.name}
      </Link>
      {this.renderUserType()}
    </ColoredSpan>
  )

  private nameHandlers = {}

  private onClickName = (observedUserId: number) =>
    memoize(this.nameHandlers, observedUserId, uid => async (e: React.MouseEvent) => {
      e.preventDefault()
      await lookAtUser({ observedUserId: uid })
      await navigate('/leasing/inbox')
    })

  private renderUserType = () => {
    let result = null
    const { userType } = this.props.user
    if (userType === UserType.SENIOR_OPERATOR) {
      result = <ColoredSpan color="sky">senior</ColoredSpan>
    } else if (userType === UserType.TEAM_LEAD) {
      result = 'team lead'
    } else if (userType === UserType.ENGINEER) {
      result = 'engineer'
    } else if (userType === UserType.ANALYST) {
      result = 'analyst'
    } else if (userType === UserType.SUPPORT) {
      result = 'support'
    } else if (userType === UserType.OPS_MANAGER) {
      result = 'ops manager'
    } else {
      return null
    }
    return (
      <>
        <Spacer width={8} />
        {result}
      </>
    )
  }

  private renderThreadCount = () => {
    if (this.isOperationsUser()) {
      return this.props.user.autoStationAssignmentEnabled ? this.props.user.unarchivedMessageCount : dash()
    }
    return dash()
  }

  private renderShift = () => {
    const opShift = this.props.user.activeOperatorShift

    if (opShift) {
      const start = opShift.shift.timeStart
      const end = opShift.shift.timeEnd

      const formattedTime = `${timeHourFmt(start, TZ_PACIFIC)}-${timeHourFmt(end, TZ_PACIFIC)}`

      return <BodyTextSpan color="steel">{formattedTime}</BodyTextSpan>
    }

    return dash()
  }

  /**
   * If you want to login as an operator in question in local development,
   * you can open an incognito window and sign in from there.
   */
  private maybeRenderClockOutButton = () => {
    const { isClockedIn } = this.props.user
    return isClockedIn ? this.renderClockOutButton() : dash()
  }

  private renderToggleAutoStation = () => {
    if (!this.showSuperUI() && this.props.loggedInUser.id !== this.props.user.id) {
      return dash()
    }

    let buttonText = 'ENABLE AUTO'
    let buttonColor = 'mint' as ColorName
    if (this.props.user.autoStationAssignmentEnabled) {
      buttonText = 'DISABLE AUTO'
      buttonColor = 'midway'
    }

    return (
      <OverrideButton
        disabled={this.state.forceDisableAutoStationAssignmentToggle}
        onClick={this.toggleAutoStation}
        color={buttonColor}
        size="small"
      >
        {buttonText}
      </OverrideButton>
    )
  }

  private isOperationsUser() {
    return (
      this.props.user.userType === 'OPERATOR' ||
      this.props.user.userType === 'SENIOR_OPERATOR' ||
      this.props.user.userType === 'TRAINING' ||
      this.props.user.userType === UserType.CUSTOMER_STEWARD ||
      this.props.user.userType === UserType.CUSTOMER_STEWARD_TRAINING
    )
  }

  /**
   * Render the user {@link Status}.
   */
  private renderStatus = () => <UserStatus user={this.props.user} />

  render() {
    const columns = [
      this.renderShift,
      this.renderName,
      this.renderThreadCount,
      this.renderOldestMessage,
      this.renderStatus,
      this.renderToggleAutoStation,
      this.maybeRenderClockOutButton,
    ]

    return (
      <Row>
        {columns.map((columnFunc: () => React.ReactNode, i: number) => {
          const opts = this.props.columns[i]
          return (
            <Cell
              data-testid={TestIds.dashboardTableCell}
              key={i}
              width={opts.width}
              align={opts.align}
              padding={opts.padding}
            >
              {columnFunc()}
            </Cell>
          )
        })}
      </Row>
    )
  }
}

export interface ColumnInfo {
  text: string
  width: number
  align?: string
  padding?: string
  adminOnly?: boolean
}

const HeaderRow = styled(BodyText)`
  cursor: default;
  width: 100%;
  padding: 8px 16px 4px 16px;
`

const Row = styled(BodyText)`
  cursor: default;
  width: 100%;
  padding: 8px 16px 4px 16px;
  color: ${Colors.silver};
  border-radius: 2px;

  &:hover {
    background: ${Colors.black};
  }
`

const Cell = styled.div`
  ${({ width, align, padding }: { width?: number; align?: string; padding?: string }) =>
    css`
      width: ${width ? width : 120}px;
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: ${align || 'unset'};
      padding: ${padding || 'unset'};
    `}
`

const OverrideButton = styled(Button)`
  box-sizing: border-box;
  border-color: transparent;
  cursor: pointer;
  border-width: 0px;

  &:hover {
    border-width: 1px;
  }
`
